import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { AlgoliaRecommendParams } from "@omni/types/api/search/algolia/recommend/algoliaRecommendParams.model";
import { AlgoliaSearchParams } from "@omni/types/api/search/algolia/search/algoliaSearchParams.model";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { cn } from "@omni/utils/styles";
import { entries, values } from "lodash-es";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { RecentsProductslider } from "@/components/Product/ProductSlider/recents/recents.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import { BreakpointMinSizes } from "@/services/breakpoint.service";
import {
    BreakpointProvider,
    useBreakpoint
} from "@/utils/breakpoints/breakpoints.provider";
import { ProductCartContext } from "@/utils/hooks/cart.hook";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { LayoutSettings } from "@/utils/swiper.utils";

import { AccessoriesProductSlider } from "./accessories/accessories.component";
import { AlgoliaSearchProductSlider } from "./algoliaSearch/algoliaSearch.component";
import { AlgoliaRecommendProductslider } from "./alogliaRecommend/algoliaRecommend.component";
import { MainProductSlider } from "./mainProducts/mainProducts.component";
import { ProductSliderContextProvider } from "./productSlider.context";
import { SkuProductSlider } from "./sku/sku.component";

export type ProductSliderProps = {
    title?: string | null;
    breakpointsConfig: {
        layoutSettings: LayoutSettings;
        hidden: Array<BreakpointMinSizes>;
    };
    productTileConfig: {
        allowInstantAddToCart: boolean;
        amountOfFeatures: number;
        showButton: boolean;
        showCategoryLink: boolean;
        useInsidePadding: boolean;
    };
    prefetchedProducts?: Array<Product<ProductFieldSet.ProductTile>>;
} & (
    | {
          source: ProductSliderSources.AlgoliaSearch;
          algoliaSearchParams: AlgoliaSearchParams;
      }
    | {
          source: ProductSliderSources.AlgoliaRecommend;
          algoliaRecommendParams: AlgoliaRecommendParams;
          sku?: string;
      }
    | {
          source: ProductSliderSources.HandPicked;
          skus: Array<string>;
      }
    | {
          source: ProductSliderSources.Accessory;
          sku: string;
      }
    | {
          source: ProductSliderSources.MainProduct;
          sku: string;
      }
    | {
          source: ProductSliderSources.Recent;
          sku: string;
      }
);

const ProductSliderContainerBase = ({ ...props }: ProductSliderProps) => {
    const dispatch = useThunkDispatch();
    const cart = useSelector(CartSelectors.getCart);
    const isLoadingCart = useSelector(CartSelectors.isLoadingCart);

    const allowInstantAddToCart = props.productTileConfig.allowInstantAddToCart;

    useEffect(() => {
        if (!allowInstantAddToCart) return;
        if (isLoadingCart) return;

        dispatch(CartModule.load());
    }, [isLoadingCart, allowInstantAddToCart, dispatch]);

    return (
        <ProductCartContext
            value={{
                allowInstantAddToCart: allowInstantAddToCart,
                cart: cart
            }}
        >
            <ProductSliderContextProvider
                title={props.title}
                productTileConfig={props.productTileConfig}
                source={props.source}
                breakpointsConfig={props.breakpointsConfig}
                prefetchedProducts={props.prefetchedProducts}
            >
                {props.source === ProductSliderSources.HandPicked && (
                    <SkuProductSlider skus={props.skus} />
                )}
                {props.source === ProductSliderSources.Accessory && (
                    <AccessoriesProductSlider sku={props.sku} />
                )}
                {props.source === ProductSliderSources.MainProduct && (
                    <MainProductSlider sku={props.sku} />
                )}
                {props.source === ProductSliderSources.Recent && (
                    <RecentsProductslider sku={props.sku} />
                )}
                {props.source === ProductSliderSources.AlgoliaRecommend && (
                    <AlgoliaRecommendProductslider
                        algoliaRecommendParams={props.algoliaRecommendParams}
                        sku={props.sku}
                    />
                )}
                {props.source === ProductSliderSources.AlgoliaSearch && (
                    <AlgoliaSearchProductSlider
                        algoliaSearchParams={props.algoliaSearchParams}
                    />
                )}
            </ProductSliderContextProvider>
        </ProductCartContext>
    );
};

const RenderProductSliderOnBreakpointWrapper = ({
    ...props
}: ProductSliderProps) => {
    const { breakpoints } = useBreakpoint();
    const isClientSide = useIsClientSide();

    const matchingBreakpoint =
        entries(breakpoints)
            .filter(([, matches]) => matches)
            .map(([breakpoint]) => breakpoint)
            .at(-1) ?? "xxs";

    const renderServer = !isClientSide;
    const renderClient =
        isClientSide &&
        Object.keys(breakpoints).length > 0 &&
        !props.breakpointsConfig.hidden.includes(
            matchingBreakpoint as BreakpointMinSizes
        );

    if (!renderServer && !renderClient) {
        return null;
    }

    return <ProductSliderContainerBase {...props} />;
};

const ProductSlider = ({
    breakpointsConfig: breakPointsConfig,
    className,
    ...props
}: ProductSliderProps & { className?: string }) => {
    const classes = values(BreakpointMinSizes).map((breakpoint) =>
        breakPointsConfig.hidden.includes(breakpoint)
            ? `d-${breakpoint}-none`
            : `d-${breakpoint}-block`
    );

    return (
        <div className={cn(className, ...classes)}>
            <BreakpointProvider>
                <RenderProductSliderOnBreakpointWrapper
                    {...props}
                    breakpointsConfig={breakPointsConfig}
                />
            </BreakpointProvider>
        </div>
    );
};

export { ProductSlider };
